import { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";

import IndeterminateCheckbox from "./IndeterminateCheckbox";

// Accessor Columns - Columns that have an underlying data model
// Display Columns - Do not have a data model, used for display arbitrary content like a row actions button, checkbox
// Grouping Columns - Used to group other columns together, (No data model either)

/**
 * useTableColumns - Hook for getting default column defs for AppTable.
 *                   This component is designed to be used for any use case, not linked to any scope.
 *
 * @param {Array<string>} headers - The array of strings to be displayed as headers for AppTable
 * @param {Array<string} hiddenHeaders - Headers you want to hide from the table by default
 * @param {function} tableDataComponent - Pass a function that returns a component you want displayed in data cells.
 * @param {boolean} selectableRows - Whether or not the first column should contain checkboxes for Selectable Rows.
 * @param {boolean} hideHeaderSelect - Whether or not to hide header select (prevents select all / unselect all option)
 * @param {boolean} columnAdd - Whether or not the last column should contain an Add button to Add Columns.
 * @param {function?} columnAddComponent - Component for column add
 * @param {function?} footerComponent - Component for table footer
 */
const useTableColumns = ({
  headers,
  hiddenHeaders = null,
  headerComponent,
  tableDataComponent,
  selectableRows,
  hideHeaderSelect = false,
  columnAdd,
  columnAddComponent,
  footerComponent,
}) => {
  const columns = useMemo(() => {
    // Ensure user passed in headers
    if (!Array.isArray(headers) || headers.length < 1) {
      return [];
    }

    const defaultColumns = [];
    const columnHelper = createColumnHelper();

    // Add Selectable Column as first columns is selectableRows is true.
    if (selectableRows) {
      defaultColumns.push(
        columnHelper.display({
          id: "selectable-rows",
          header: ({ table }) =>
            hideHeaderSelect ? null : (
              <IndeterminateCheckbox
                {...{
                  checked: table.getIsAllRowsSelected(),
                  indeterminate: table.getIsSomeRowsSelected(),
                  onChange: table.getToggleAllRowsSelectedHandler(),
                }}
              />
            ),
          cell: ({ row }) => (
            <IndeterminateCheckbox
              {...{
                checked: row.getIsSelected(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          ),
        })
      );
    }

    // Adding the main data columns to the table.
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i];

      // Don't add hidden headers (columns) to the table.
      if (hiddenHeaders && hiddenHeaders.includes(header)) {
        continue;
      }

      defaultColumns.push(
        columnHelper.accessor(header, {
          id: header,
          cell: (item) => tableDataComponent(item),
          header: (item) => headerComponent(item),
          ...(footerComponent && { footer: (item) => footerComponent(item) }),
        })
      );
    }

    // Add Column Add button if columnAdd is true
    if (columnAdd) {
      if (!columnAddComponent) {
        console.error(
          "Invalid columnAddComponent in useTableColumns. A functional component must be passed in when columnAdd = true."
        );
      }

      defaultColumns.push(
        columnHelper.display({
          id: "column-add",
          header: ({ table }) => (
            <div className="d-flex justify-content-center">
              {columnAddComponent(table)}
            </div>
          ),
        })
      );
    }

    return defaultColumns;
  }, [
    headers,
    selectableRows,
    columnAdd,
    hiddenHeaders,
    columnAddComponent,
    headerComponent,
    tableDataComponent,
    footerComponent,
    hideHeaderSelect,
  ]);

  return { columns };
};

export default useTableColumns;
